import type { FieldPath } from 'react-hook-form';

import Button from '@polyai/ui/components/atoms/Button';
import HelpText from '@polyai/ui/components/atoms/HelpText';
import IconButton from '@polyai/ui/components/atoms/IconButton';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@polyai/ui/components/atoms/Icons';
import { CheckboxOption } from '@polyai/ui/components/atoms/Select/components/CheckboxOption';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';

import { FormSelect, FormToggle } from 'components/molecules/Form';
import JupiterCard from 'components/molecules/JupiterCard';
import { DATA_ACCESS } from 'components/organisms/DataAccess/DataAccess.uiCopy';
import useApiConfigDelete from 'hooks/api/apiConfig/useApiConfigDelete';
import usePersistentCollapseStates from 'hooks/usePersistentCollapseStates';
import type { ApiConfigFormInput } from '../..';
import * as Styled from './ApiConfigCard.styled';
import API_CONFIG_CARD from './ApiConfigCard.uiCopy';

type MetricOption = { value: string; label: string; isCustom?: boolean };

interface ApiConfigCardProps {
  projectId: string;
  title?: string;
  metricOptions?: MetricOption[];
  controlPrefix: `configs.${string}`;
}

const ApiConfigCard = ({
  projectId,
  title,
  metricOptions = [],
  controlPrefix,
}: ApiConfigCardProps) => {
  const { mutate: deleteConfig, isLoading: isDeletingConfig } =
    useApiConfigDelete();

  const { getState, toggleState } = usePersistentCollapseStates(
    'api-config-cards',
    true,
  );

  const changedMetricsKey =
    `${controlPrefix}.changed_metrics` as FieldPath<ApiConfigFormInput>;
  const showTranscriptsKey =
    `${controlPrefix}.show_transcripts` as FieldPath<ApiConfigFormInput>;

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleState(projectId);
  };

  return (
    <JupiterCard cols={['large']}>
      <Styled.CardContainer>
        <Styled.Header $isCollapsed={getState(projectId)}>
          <Styled.CardTitle>{title}</Styled.CardTitle>
          <Styled.HeaderButtonsWrapper>
            <IconButton onClick={e => handleToggleCollapse(e)}>
              {getState(projectId) ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </IconButton>
          </Styled.HeaderButtonsWrapper>
        </Styled.Header>

        {!getState(projectId) && (
          <Stack direction="column" spacing="xs2">
            <HelpText help={API_CONFIG_CARD.responseMetrics.help}>
              <Styled.TitleText>
                {API_CONFIG_CARD.responseMetrics.label}{' '}
                <Styled.HelperText>
                  {API_CONFIG_CARD.responseMetrics.optional}
                </Styled.HelperText>
              </Styled.TitleText>
            </HelpText>

            <FormSelect<ApiConfigFormInput, MetricOption>
              closeMenuOnSelect={false}
              components={{
                Option: CheckboxOption,
              }}
              controlKey={changedMetricsKey}
              hideSelectedOptions={false}
              options={metricOptions}
              selectAllOption={{
                label: DATA_ACCESS.modal.select.metrics.all.label,
                value: '*',
              }}
              isMulti
              withOptionSelectedIndicator
            />
            <Stack direction="row" className="mt-xs2" spacing="xs3">
              <FormToggle<ApiConfigFormInput>
                aria-label="show_transcripts"
                controlKey={showTranscriptsKey}
              />
              <HelpText help={API_CONFIG_CARD.conversationTranscript.help}>
                <Text>{API_CONFIG_CARD.conversationTranscript.label}</Text>
              </HelpText>
            </Stack>
            <Stack className="mt-sm1">
              <Button
                disabled={isDeletingConfig}
                label={API_CONFIG_CARD.clearConfig.label}
                variant="SECONDARY"
                onClick={() => deleteConfig(projectId)}
              />
            </Stack>
          </Stack>
        )}
      </Styled.CardContainer>
    </JupiterCard>
  );
};

export default ApiConfigCard;
